import type { ReactNode } from 'react';

/**
 * 給金融術語加白話 tooltip：滑鼠停留 / 手機點擊顯示說明
 * 用法：<Term tip="本益比，越低代表越便宜">P/E</Term>
 *
 * 用瀏覽器原生 title 屬性 + 虛線底，零依賴。
 * 同時保留 aria 給螢幕閱讀器。
 */
export function Term({ tip, children }: { tip: string; children: ReactNode }) {
  return (
    <abbr
      title={tip}
      aria-label={tip}
      className="cursor-help no-underline border-b border-dashed border-(--text-muted)/50 hover:border-(--accent)/70 hover:text-(--accent) transition-colors"
    >
      {children}
    </abbr>
  );
}

/** 預先定義的常用術語白話翻譯 */
export const TERMS = {
  PE: '本益比 = 股價 ÷ 每股盈餘。簡單說：花多少錢買到 1 元的獲利。15 以下通常便宜、30 以上偏貴。',
  ForwardPE: '預估本益比，用分析師預測的明年 EPS 算的。比 P/E 更前瞻。',
  PB: '股價淨值比 = 股價 ÷ 每股淨值。看股價是不是便宜。1 以下通常算便宜、3 以上偏貴。',
  EPS: '每股盈餘 = 公司賺的錢 ÷ 總股數。每張股票一年幫你賺多少錢。',
  ROE: '股東權益報酬率。公司用自己的錢賺錢的效率，15% 以上算好、20% 以上算優。',
  ROA: '資產報酬率。公司用所有資產（含借的）賺錢的效率。',
  ProfitMargin: '淨利率 = 淨利 ÷ 營收。賺 100 元真正放進口袋幾元。',
  GrossMargin: '毛利率 = 毛利 ÷ 營收。每賣 100 元扣掉成本剩多少。',
  OperatingMargin: '營業利益率，扣掉本業成本與管銷費用後的賺錢能力。',
  DividendYield: '股息殖利率 = 一年配息 ÷ 股價。買進來一年領多少 % 利息。',
  MarketCap: '市值 = 股價 × 總股數。整家公司的價格牌。',
  RSI: 'RSI 14 日相對強弱指標，0-100。70 以上太熱、30 以下太冷，常用來抓反彈或拉回。',
  MACD: 'MACD 看趨勢動能：紅柱越高越多頭、綠柱越深越空頭。',
  ATR: '平均真實波動。一天大概會漲跌多少錢，看波動空間。',
  BB: '布林通道。價格的合理區間，碰上下軌常代表偏激烈。',
  Stoch: 'KD 隨機指標。80 以上偏熱、20 以下偏冷。',
  OBV: '能量潮。看資金有沒有偷偷流進或流出。',
  Volume: '成交量。今天買賣有多熱絡，量大代表大家有興趣。',
  VolRatio: '量比 = 近 5 天平均量 ÷ 全段平均量。大於 1 代表最近放量。',
  TargetPrice: '分析師平均目標價。華爾街分析師預測未來 12 個月應該漲到多少。',
  Recommendation: '分析師建議。strong_buy 強力買進、buy 買進、hold 持有、sell 賣出。',
} as const;
